export interface TrackedPromise<TResult, TArgs extends unknown[]> {
	/**
	 * Starts a fresh call.
	 * `tracked` / `lastResult` / `lastError` are updated only when the **latest** started call settles, so stale completions do not overwrite newer state.
	 */
	call(this: void, ...args: TArgs): Promise<TResult>;
	/** Call this instead of {@link call} when you want to reuse the currently pending promise (if it is pending). */
	waitForCurrentOrCall(this: void, ...args: TArgs): Promise<TResult>;

	/** State and/or response from the latest call. */
	tracked: {
		/** Args from the last call, if any. */
		lastArgs?: TArgs;
		/** Latest error that happened, if any. */
		lastError?: unknown;
		/** Promise from the last call, if any */
		lastPromise?: Promise<TResult>;
		/** Result from the latest successful call */
		lastResult?: TResult;
	} & (
		| {
				status: "initial";
		  }
		| {
				args: TArgs;
				promise: Promise<TResult>;
				status: "pending";
		  }
		| {
				args: TArgs;
				error: unknown;
				status: "rejected";
		  }
		| {
				args: TArgs;
				result: TResult;
				status: "resolved";
		  }
	);
}

/**
 * Tracks the progress/status and/or response of the promise which gets returned from the callback.
 *
 * The callback can be called multiple times.
 */
export function createTrackedPromise<TResult, TArgs extends unknown[]>(
	callback: (...args: TArgs) => Promise<TResult>,
): TrackedPromise<TResult, TArgs> {
	let tracked = $state<TrackedPromise<TResult, TArgs>["tracked"]>({
		status: "initial",
	});
	let lastResult = $state<TResult | undefined>();
	let callGeneration = 0;

	async function call(...args: TArgs): Promise<TResult> {
		const thisGeneration = ++callGeneration;
		const promise = callback(...args);
		try {
			tracked = { ...tracked, lastArgs: args, args, lastPromise: promise, promise, status: "pending" };
			const result = await promise;
			if (thisGeneration === callGeneration) {
				lastResult = structuredClone($state.snapshot(result) as TResult | undefined);
				tracked = { ...tracked, args, lastResult, result, status: "resolved" };
			}
			return result;
		} catch (error) {
			if (thisGeneration === callGeneration) {
				tracked = { ...tracked, lastError: error, args, error, status: "rejected" };
			}
			throw error;
		}
	}

	async function waitForCurrentOrCall(...args: TArgs): Promise<TResult> {
		if (tracked.status === "pending") {
			return await tracked.promise;
		}
		return await call(...args);
	}

	return {
		call,
		waitForCurrentOrCall,
		get tracked(): typeof tracked {
			return {
				...tracked,
				lastResult,
			};
		},
	};
}
